import React, { useState, useEffect, useContext } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import Board from 'react-trello';
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { useHistory } from 'react-router-dom';
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import { Badge, Tooltip, Typography, Button, TextField, Box, Paper } from "@material-ui/core";
import { format, isSameDay, parseISO } from "date-fns";
import { Can } from "../../components/Can";
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ModernDatePicker from "../../components/ModernDatePicker";
import moment from "moment";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2),
    height: "100vh",
    backgroundColor: theme.palette.background.default,
  },
  kanbanContainer: {
    width: "100%",
    height: "calc(100vh - 120px)",
    overflow: "hidden", // Let react-trello handle overflow
    padding: theme.spacing(1),
    "& .react-trello-lane": {
      backgroundColor: "rgba(235, 236, 240, 0.5)",
      borderRadius: "12px",
      margin: "0 8px",
      padding: "12px",
      border: "1px solid rgba(0,0,0,0.05)",
      "& header": {
        padding: "0 0 12px 0",
        "& span": {
          fontSize: "1.1rem",
          fontWeight: 600,
          color: theme.palette.text.primary,
        }
      }
    }
  },
  filterContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    maxWidth: "1400px",
    marginBottom: theme.spacing(1),
    flexWrap: "wrap",
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: "12px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },
  dateInput: {
    marginRight: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
    }
  },
  searchButton: {
    borderRadius: "8px",
    background: "linear-gradient(45deg, #6517ab 30%, #8E2DE2 90%)",
    color: "white",
    padding: "8px 24px",
    boxShadow: "0 3px 5px 2px rgba(101, 23, 171, .3)",
    "&:hover": {
      background: "linear-gradient(45deg, #5a1499 30%, #7e28c8 90%)",
    }
  },
  addButton: {
    borderRadius: "8px",
    background: "linear-gradient(45deg, #8E2DE2 30%, #6517ab 90%)",
    color: "white",
    padding: "8px 24px",
    boxShadow: "0 3px 5px 2px rgba(142, 45, 226, .3)",
    "&:hover": {
      background: "linear-gradient(45deg, #7e28c8 30%, #5a1499 90%)",
    }
  },
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "10px",
    padding: "12px",
    marginBottom: "8px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer",
    borderLeft: "4px solid transparent",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 12px rgba(0,0,0,0.08)",
    }
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  cardBody: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    lineHeight: "1.4",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "12px",
    paddingTop: "8px",
    borderTop: "1px solid rgba(0,0,0,0.05)",
  },
  badgeUser: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontSize: "0.7rem",
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: "4px",
    textTransform: "uppercase",
  },
  time: {
    fontSize: "0.75rem",
    color: theme.palette.text.hint,
  },
  unRead: {
    color: theme.palette.success.main,
    fontWeight: "bold",
  }
}));

const KanbanCard = ({ id, title, label, description, tags, onClick }) => {
  const classes = useStyles();
  
  return (
    <div className={classes.card} onClick={onClick}>
      <div className={classes.cardHeader}>
        <Typography className={classes.cardTitle}>
          {title}
        </Typography>
        <Typography className={classes.time}>
          {label}
        </Typography>
      </div>
      <div className={classes.cardBody}>
        {description}
      </div>
    </div>
  );
};

const Kanban = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [file, setFile] = useState({ lanes: [] });
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

  const jsonString = user.queues.map(queue => queue.UserQueue.queueId);

  useEffect(() => {
    fetchTags();
  }, [user]);

  const fetchTags = async () => {
    try {
      const response = await api.get("/tag/kanban/");
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets();
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTickets = async () => {
    try {
      const { data } = await api.get("/ticket/kanban", {
        params: {
          queueIds: JSON.stringify(jsonString),
          startDate: startDate,
          endDate: endDate,
        }
      });
      setTickets(data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;
    const onAppMessage = (data) => {
      if (data.action === "create" || data.action === "update" || data.action === "delete") {
        fetchTickets();
      }
    };
    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket, startDate, endDate]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleStartDateChange = (value) => {
    setStartDate(moment(value).format("YYYY-MM-DD"));
  };

  const handleEndDateChange = (value) => {
    setEndDate(moment(value).format("YYYY-MM-DD"));
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", fontSize: "18px" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", fontSize: "18px" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", fontSize: "18px" }} />
      default:
        return null;
    }
  };

  const popularCards = (jsonString) => {
    const filteredTickets = tickets.filter(ticket => ticket.tags.length === 0);

    const lanes = [
      {
        id: "lane0",
        title: i18n.t("tagsKanban.laneDefault"),
        label: filteredTickets.length.toString(),
        cards: filteredTickets.map(ticket => ({
          id: ticket.id.toString(),
          title: (
            <>
              {IconChannel(ticket.channel)}
              <span style={{ marginLeft: 8 }}>{ticket.contact.name}</span>
            </>
          ),
          label: isSameDay(parseISO(ticket.updatedAt), new Date()) ? 
            format(parseISO(ticket.updatedAt), "HH:mm") : 
            format(parseISO(ticket.updatedAt), "dd/MM/yyyy"),
          description: (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" style={{ fontWeight: 600, color: theme.palette.text.primary }}>
                {ticket.contact.number}
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ticket.lastMessage || "Sem mensagens"}
              </Typography>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {ticket?.user && (
                   <span className={classes.badgeUser}>{ticket.user?.name}</span>
                )}
                <Button
                  size="small"
                  startIcon={<VisibilityIcon />}
                  style={{
                    color: "#8E2DE2",
                    fontSize: "0.7rem",
                    padding: "2px 8px",
                    textTransform: "none",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(ticket.uuid);
                  }}
                >
                  Ver
                </Button>
              </div>
            </div>
          ),
          draggable: true,
          metadata: { uuid: ticket.uuid }
        })),
        style: { backgroundColor: "rgba(235, 236, 240, 0.5)" }
      },
      ...tags.map(tag => {
        const filteredTickets = tickets.filter(ticket => {
          const tagIds = ticket.tags.map(tag => tag.id);
          return tagIds.includes(tag.id);
        });

        return {
          id: tag.id.toString(),
          title: tag.name,
          label: filteredTickets?.length.toString(),
          cards: filteredTickets.map(ticket => ({
            id: ticket.id.toString(),
            title: (
              <>
                {IconChannel(ticket.channel)}
                <span style={{ marginLeft: 8 }}>{ticket.contact.name}</span>
              </>
            ),
            label: isSameDay(parseISO(ticket.updatedAt), new Date()) ? 
              format(parseISO(ticket.updatedAt), "HH:mm") : 
              format(parseISO(ticket.updatedAt), "dd/MM/yyyy"),
            description: (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" style={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  {ticket.contact.number}
                </Typography>
                <Typography variant="body2" style={{ marginTop: 4, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ticket.lastMessage || "Sem mensagens"}
                </Typography>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {ticket?.user && (
                    <span className={classes.badgeUser}>{ticket.user?.name}</span>
                  )}
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    style={{
                      color: "#8E2DE2",
                      fontSize: "0.7rem",
                      padding: "2px 8px",
                      textTransform: "none",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick(ticket.uuid);
                    }}
                  >
                    Ver
                  </Button>
                </div>
              </div>
            ),
            draggable: true,
            metadata: { uuid: ticket.uuid }
          })),
          style: { backgroundColor: tag.color + "22", borderTop: `4px solid ${tag.color}` }
        };
      }),
    ];

    setFile({ lanes });
  };

  const handleCardClick = (uuid) => {
    history.push('/tickets/' + uuid);
  };

  useEffect(() => {
    popularCards(jsonString);
  }, [tags, tickets]);

  const handleCardMove = async (cardId, sourceLaneId, targetLaneId) => {
    try {
      await api.delete(`/ticket-tags/${targetLaneId}`);
      // toast.success('Ticket Tag Removido!'); // Removed to make it cleaner
      await api.put(`/ticket-tags/${targetLaneId}/${sourceLaneId}`);
      toast.success('Ticket Tag Adicionado com Sucesso!');
      await fetchTickets(jsonString);
      popularCards(jsonString);
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddConnectionClick = () => {
    history.push('/tagsKanban');
  };

  return (
    <div className={classes.root}>
      <div className={classes.filterContainer}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <ModernDatePicker
            label="Data de início"
            value={startDate ? moment(startDate).toDate() : null}
            onChange={handleStartDateChange}
            fullWidth={false}
          />
          <ModernDatePicker
            label="Data de fim"
            value={endDate ? moment(endDate).toDate() : null}
            onChange={handleEndDateChange}
            fullWidth={false}
          />
          <Button
            startIcon={<SearchIcon />}
            className={classes.searchButton}
            onClick={handleSearchClick}
          >
            Buscar
          </Button>
        </div>
        <Can role={user.profile} perform="dashboard:view" yes={() => (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            className={classes.addButton}
            onClick={handleAddConnectionClick}
          >
            {'Novo Quadro'}
          </Button>
        )} />
      </div>
      <div className={classes.kanbanContainer}>
        {file.lanes.length > 0 ? (
          <Board
            data={file}
            onCardMoveAcrossLanes={handleCardMove}
            onCardClick={(cardId, metadata) => handleCardClick(metadata.uuid)}
            style={{ backgroundColor: 'transparent' }}
            cardStyle={{ borderRadius: '10px', padding: '0px', marginBottom: '8px' }}
            laneStyle={{ maxHeight: '100%', borderRadius: '12px' }}
            components={{ Card: KanbanCard }}
          />
        ) : (
          <Typography align="center" style={{ marginTop: '20px' }}>
            {i18n.t("kanban.loading") || "Carregando..."}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default Kanban;